export interface NotesEditorState {
    patientId: string;
    text: string;
    baseline: string;
}

export type NotesEditorAction =
    | { type: 'server'; patientId: string; notes: string | null }
    | { type: 'edit'; text: string }
    | { type: 'discard' }
    | { type: 'saved'; patientId: string; notes: string };

export interface NotesLockInput {
    checking: boolean;
    dirty: boolean;
    saving: boolean;
}

export function initNotesEditor(patientId: string, notes: string | null): NotesEditorState {
    const text = notes ?? '';
    return { patientId, text, baseline: text };
}

export function isNotesDirty(state: NotesEditorState): boolean {
    return state.text !== state.baseline;
}

// A clean editor locks while DM is checked so nobody types into text about to be replaced.
export function isNotesReadOnly({ checking, dirty, saving }: NotesLockInput): boolean {
    return saving || (checking && !dirty);
}

// Unsaved edits are never replaced; the baseline still moves so Discard returns to the newest text.
function applyServerNotes(
    state: NotesEditorState,
    patientId: string,
    notes: string | null
): NotesEditorState {
    if (patientId !== state.patientId) return initNotesEditor(patientId, notes);
    if (notes === null || notes === state.baseline) return state;
    if (isNotesDirty(state)) return { ...state, baseline: notes };
    return { ...state, text: notes, baseline: notes };
}

export function notesEditorReducer(
    state: NotesEditorState,
    action: NotesEditorAction
): NotesEditorState {
    switch (action.type) {
        case 'server':
            return applyServerNotes(state, action.patientId, action.notes);
        case 'edit':
            return action.text === state.text ? state : { ...state, text: action.text };
        case 'discard':
            return isNotesDirty(state) ? { ...state, text: state.baseline } : state;
        case 'saved':
            if (action.patientId !== state.patientId) return state;
            return initNotesEditor(action.patientId, action.notes);
    }
}
